import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Brand from './Brand';
import './workspace-arrival.css';

const MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const ARRIVAL_DURATION = 2400;
const UNLOCK_DURATION = 2000;
const DEPARTURE_DURATION = 2200;
const REDUCED_DURATION = 180;

function useArrivalMotion() {
  const [reduced, setReduced] = useState(() => !!window.matchMedia?.(MOTION_QUERY).matches);
  useEffect(() => {
    const media = window.matchMedia?.(MOTION_QUERY);
    if (!media) return;
    const change = event => setReduced(event.matches);
    setReduced(media.matches);
    if (media.addEventListener) media.addEventListener('change', change);
    else media.addListener?.(change);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', change);
      else media.removeListener?.(change);
    };
  }, []);
  return reduced;
}

// identity is a stable, unique arrival ticket. name must already be a public nickname.
export default function WorkspaceArrival({ name, kind = 'signin', identity, onDone, owner = false }) {
  const reduced = useArrivalMotion();
  const latest = useRef({ identity, onDone }), started = useRef(null), completed = useRef(new Set());
  const [finished, setFinished] = useState(null);
  latest.current = { identity, onDone };
  const departing = kind === 'signout';
  const duration = reduced ? REDUCED_DURATION : departing ? DEPARTURE_DURATION : kind === 'unlock' ? UNLOCK_DURATION : ARRIVAL_DURATION;
  const publicName = departing ? '' : owner ? 'L' : typeof name === 'string' ? name.trim().replace(/\s+/g, ' ').slice(0, 48) : '';
  const presented = useRef(null);
  if (!presented.current || presented.current.identity !== identity) presented.current = { identity, name: publicName };
  const nickname = presented.current.name;
  const greeting = departing ? 'Until next time.' : `Welcome back${nickname ? ',' : '.'}`;
  const message = departing ? 'You’re signed out.' : kind === 'unlock' ? 'Back to your flow.' : 'Your journal is ready.';

  useEffect(() => {
    if (identity === undefined || identity === null || identity === '' || completed.current.has(identity)) return;
    if (started.current?.identity !== identity) started.current = { identity, at: Date.now() };
    let disposed = false, timer;
    const finish = () => {
      if (disposed || latest.current.identity !== identity || completed.current.has(identity)) return;
      completed.current.add(identity);
      clearTimeout(timer);
      setFinished(identity);
      latest.current.onDone?.(identity);
    };
    const visibility = () => { if (document.hidden) finish(); };
    document.addEventListener('visibilitychange', visibility);
    if (document.hidden) finish();
    else timer = setTimeout(finish, Math.max(0, duration - (Date.now() - started.current.at)));
    return () => { disposed = true; clearTimeout(timer); document.removeEventListener('visibilitychange', visibility); };
  }, [identity, duration]);

  if (identity === undefined || identity === null || identity === '' || document.hidden || finished === identity || completed.current.has(identity)) return null;
  return createPortal(<>
    <div key={identity} className="tl-workspace-arrival" data-motion={reduced ? 'reduced' : 'full'} data-kind={kind} style={{ '--tl-arrival-duration': `${duration}ms` }} aria-hidden="true">
      <div className="tl-arrival-bloom"/>
      <div className="tl-arrival-horizon"/>
      <div className="tl-arrival-portal">
        <svg viewBox="0 0 600 600" fill="none" focusable="false">
          <circle className="tl-arrival-track" cx="300" cy="300" r="252"/>
          <circle className="tl-arrival-track-inner" cx="300" cy="300" r="236"/>
          <path className="tl-arrival-arc" d="M122 122a252 252 0 0 1 356 0M478 478a252 252 0 0 1-356 0"/>
        </svg>
      </div>
      <div className="tl-arrival-portal tl-arrival-portal-inner">
        <svg viewBox="0 0 600 600" fill="none" focusable="false">
          <circle className="tl-arrival-orbit-line" cx="300" cy="300" r="220" strokeDasharray="86 260 28 317"/>
          <circle className="tl-arrival-orbit-point" cx="300" cy="80" r="2.5"/>
        </svg>
      </div>
      <div className="tl-arrival-copy">
        <div className="tl-arrival-brand"><Brand size={30}/></div>
        <span className="tl-arrival-greeting"><span>{greeting}</span></span>
        <h1 data-long-name={nickname.length > 20 || undefined} data-nickname={!!nickname || undefined}><span>{departing ? 'See you soon.' : nickname || 'Your next chapter.'}</span></h1>
        <p>{message}</p>
        <span className="tl-arrival-rule"><span/></span>
      </div>
    </div>
    <span className="tl-sr-only tl-arrival-announcement" role="status" aria-live="polite" aria-atomic="true">{departing ? 'Until next time.' : `Welcome back${nickname ? `, ${nickname}` : ''}.`} {message}</span>
  </>, document.body);
}
